/**
 * **运行时清点「真的存在哪些 CSS 变量」**。
 *
 * ## 为什么需要
 *
 * 令牌型主题的**头号失败模式是静默的**：把 `--np-accent-soft` 拼成
 * `--np-accent-sotf`，CSS 自定义属性不会报错 —— 它只是**没人用**，
 * 于是「改了没反应」，而人会以为是自己没保存。
 *
 * `verify:theme` 在**构建期**对这笔账（拿 `theme.css` 的文本比对）。
 * 但这个文件解决另一半：**运行期**。主题可能来自
 * 一个导入的文件、一段粘进面板的 CSS、或者将来从服务器拉的主题包 ——
 * 那些都不在构建期的视野里。
 *
 * ## 怎么拿到清单
 *
 * 直接问浏览器：遍历 `document.styleSheets`，把每条样式规则里
 * 以 `--` 开头的属性名收进来。这样**连主题自己注入的那段
 * `<style data-np-theme…>` 和用户在面板里写的 CSS 也算数** ——
 * 也就是「你刚定义了一个变量，然后就能用它」。
 *
 * ⚠️ 跨域样式表读 `cssRules` 会**抛异常**（不是返回空）。
 * 所以每一张表都要单独 try/catch：一张外链字体 CSS 不能让整个
 * 清点功能失效。
 *
 * ⚠️ 结果**缓存**。遍历全部规则在大型样式表上是毫秒级的开销，
 * 但这个函数是喂给 computed 的，每次按键都重跑会明显卡。
 * 缓存键是「样式表数量 + 主题 id」，主题一变就失效。
 */
import { activeThemeId, previewDoc, resolvedTheme } from './registry'

let cache: Set<string> | null = null
let cacheKey = ''

/** 这个变量名在样式表里真的有定义吗（含主题注入的那段） */
export function hasToken(name: string): boolean {
  return resolvedTokenNames().has(name)
}

/**
 * 当前页面上出现过的全部自定义属性名。
 *
 * ⚠️ 不要在渲染循环里直接调 —— 它可能触发一次全表遍历。
 * 走 `hasToken()` 更省，但两者共用同一份缓存。
 */
export function resolvedTokenNames(): Set<string> {
  const key = `${activeThemeId.value}|${previewDoc.value?.id ?? ''}`
  if (cache && cacheKey === key) return cache

  const names = new Set<string>()

  /* 主题自己声明的（哪怕样式表还没被遍历到，也不该被判成"拼错"） */
  for (const k of Object.keys(resolvedTheme.value.tokens)) names.add(k)

  if (typeof document !== 'undefined') {
    for (const sheet of Array.from(document.styleSheets)) {
      collectFromSheet(sheet, names, 0)
    }
  }

  cache = names
  cacheKey = key
  return names
}

/** 主题换了/换了草稿之后把缓存丢掉 —— 由 `registry` 的 watch 调用 */
export function invalidateTokenNames(): void {
  cache = null
  cacheKey = ''
}

function collectFromSheet(sheet: CSSStyleSheet, out: Set<string>, depth: number): void {
  /* 媒体查询可以嵌套，但不会无限深；给个上限防止畸形样式表把栈打穿 */
  if (depth > 6) return
  let rules: CSSRuleList
  try {
    rules = sheet.cssRules
  } catch {
    /* 跨域样式表 —— 读不到是正常的，不是错误 */
    return
  }
  for (const rule of Array.from(rules)) {
    collectFromRule(rule, out, depth)
  }
}

function collectFromRule(rule: CSSRule, out: Set<string>, depth: number): void {
  /* 1 = CSSStyleRule（普通规则） */
  const style = (rule as CSSStyleRule).style
  if (style && typeof style.length === 'number' && style.length > 0) {
    for (let i = 0; i < style.length; i += 1) {
      const prop = style.item(i)
      if (prop.startsWith('--')) out.add(prop)
    }
  }
  /* 4 = CSSMediaRule / 12 = CSSSupportsRule / 7 = CSSKeyframesRule… —— 递归 */
  const nested = (rule as CSSGroupingRule).cssRules
  if (nested) collectFromSheet({ cssRules: nested } as CSSStyleSheet, out, depth + 1)
}
